import React from 'react';
import {
  FaUserTie,
  FaHourglassHalf,
  FaCog,
  FaFileAlt,
  FaCheckDouble,
  FaCalendarCheck,
  FaClipboardCheck,
  FaFileInvoice,
  FaProjectDiagram,
  FaBuilding,
  FaUsers,
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { MISDashboard } from '../../../types/dashboard';
import { StatCard, SectionCard, MiniStat, StatusBadge, DataTable, formatDate, formatWorkflowStatus } from './ui';
import { ApprovalsSection } from './approvals-section';
import { BarChart } from './charts';

/**
 * MIS Dashboard view — extended to match the "Dashboard 140626 Mukul" (MIS tab) spec.
 *
 * Replace the existing `MISView` export inside `role-dashboards.tsx` with this component
 * (keep this file in the same `components/role-dashboards` folder so the './ui' and
 * './charts' relative imports resolve). New data comes from the extended
 * `getMISDashboard` controller — fields are read defensively so the view still renders
 * if the backend hasn't been updated yet.
 *
 * NOTE: the new fields aren't in the `MISDashboard` TS type yet, so they're read via
 * `(data as any)`. Add them to types/dashboard.ts when convenient for full typing.
 */

const toNumber = (v: unknown) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

// Pivot flat grouped rows ({ '<dim>.<name>': x, survey_workflow_status, count }) into
// one row per activity/project with a column per workflow status.
const pivotLevel = (
  rows: any[] | undefined,
  nameKeys: string[],
  statuses: string[]
): Array<{ name: string } & Record<string, number>> => {
  const map = new Map<string, { name: string } & Record<string, number>>();
  for (const r of rows || []) {
    const name =
      nameKeys.map((k) => r?.[k]).find((v) => v != null && v !== '') || 'Unassigned';
    const key = String(name);
    const existing = map.get(key) || ({ name: key } as { name: string } & Record<string, number>);
    const status = String(r?.survey_workflow_status ?? '');
    existing[status] = (existing[status] || 0) + toNumber(r?.count);
    map.set(key, existing);
  }
  // ensure every status column exists
  return Array.from(map.values()).map((row) => {
    statuses.forEach((s) => {
      if (row[s] == null) row[s] = 0;
    });
    return row;
  });
};

const PENDING_STATUSES = [
  'mis_report_assign_pending',
  'report_maker_assigned',
  'report_in_progress',
  'report_completed',
  'validator_assigned',
  'validation_in_progress',
  'report_finalized',
];
const REJECT_STATUSES = ['ta_rejected', 'report_maker_rejected', 'validation_rejected'];
const PROGRESS_COLUMNS = [
  'field_work_completed',
  'ta_review_completed',
  'report_completed',
  'report_finalized',
  'report_sent_to_client',
];

interface MISViewProps {
  data: MISDashboard;
}

export const MISView: React.FC<MISViewProps> = ({ data }) => {
  const d = data as any;
  const { summary, unassigned_lists, assigned_pending_lists, billing } = data;

  const responsibility = d.responsibility_overview;
  const approvals = d.approvals;
  const pendingWork = d.pending_work;
  const todayProgress = d.today_progress;
  const rejectionPending = d.rejection_pending;

  const rmAssignPending = unassigned_lists?.report_maker_assign_pending ?? d.pending_assignment ?? [];
  const validatorAssignPending = unassigned_lists?.validator_assign_pending ?? [];
  const sentToClientPending = unassigned_lists?.sent_to_client_pending ?? [];
  const rmWise =
    assigned_pending_lists?.report_maker_wise ??
    (d.report_makers ?? []).map((r: any) => ({ report_designer_username: r.report_designer_username, count: r.total_sites }));
  const validatorWise = assigned_pending_lists?.validator_wise ?? [];

  // Pre-pivot the activity/project breakdowns
  const pendingActivity = pivotLevel(pendingWork?.activity_level, ['activity.activity_name'], PENDING_STATUSES);
  const pendingProject = pivotLevel(
    pendingWork?.project_level,
    ['project.project_short_name', 'project.project_name'],
    PENDING_STATUSES
  );
  const rejectActivity = pivotLevel(rejectionPending?.activity_level, ['activity.activity_name'], REJECT_STATUSES);
  const rejectProject = pivotLevel(
    rejectionPending?.project_level,
    ['project.project_short_name', 'project.project_name'],
    REJECT_STATUSES
  );

  const levelColumns = (firstLabel: string, statuses: string[]) => [
    { key: 'name', label: firstLabel },
    ...statuses.map((s) => ({ key: s, label: formatWorkflowStatus(s), align: 'center' as const })),
  ];

  return (
    <div className="space-y-6">
      {/* ===== Responsibility Overview ===== */}
      {responsibility && (
        <SectionCard title="Responsibility Overview" icon={FaProjectDiagram}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard title="Assigned Projects" value={responsibility.assigned_projects ?? 0} icon={FaProjectDiagram} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
            <StatCard title="Direct Team" value={responsibility.direct_team ?? 0} icon={FaUsers} iconBgColor="bg-indigo-100" iconColor="text-indigo-600" />
            <StatCard title="All Team" value={responsibility.all_team ?? 0} icon={FaUsers} iconBgColor="bg-violet-100" iconColor="text-violet-600" />
            <StatCard title="Sent to Client (Month)" value={responsibility.report_sent_to_client_current_month ?? 0} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
            <StatCard title="Submission Pending" value={responsibility.report_submission_pending_total ?? 0} icon={FaHourglassHalf} iconBgColor="bg-orange-100" iconColor="text-orange-600" />
          </div>
        </SectionCard>
      )}

      {/* ===== Approvals ===== */}
      <ApprovalsSection approvals={approvals} />

      {/* ===== Summary stage pipeline (kept) ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard title="Total Assigned" value={summary.total_assigned} icon={FaClipboardCheck} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
        <StatCard title="Pending RM Assign" value={summary.pending_rm_assignment} icon={FaHourglassHalf} iconBgColor="bg-yellow-100" iconColor="text-yellow-600" />
        <StatCard title="RM Assigned" value={summary.report_maker_assigned} icon={FaUserTie} iconBgColor="bg-orange-100" iconColor="text-orange-600" />
        <StatCard title="Report In Progress" value={summary.report_in_progress} icon={FaFileAlt} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
        <StatCard title="Report Completed" value={summary.report_completed ?? (summary as any).reports_completed ?? 0} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
        <StatCard title="This Month Done" value={summary.current_month_done ?? 0} icon={FaCalendarCheck} iconBgColor="bg-teal-100" iconColor="text-teal-600" />
      </div>

      {/* ===== Operations Overview → Pending Work ===== */}
      {pendingWork && (
        <SectionCard title="Pending Work — Group Stage" icon={FaHourglassHalf}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MiniStat label="Report Preparation Pending" value={pendingWork.group_stage?.report_preparation_pending ?? 0} color="blue" />
            <MiniStat label="Validation Pending" value={pendingWork.group_stage?.validation_pending ?? 0} color="yellow" />
            <MiniStat label="Report Submission Pending" value={pendingWork.group_stage?.report_submission_pending ?? 0} color="green" />
          </div>

          {pendingWork.by_workflow_status && (
            <div className="mt-4 h-64">
              <BarChart
                labels={Object.keys(pendingWork.by_workflow_status).map((k) => formatWorkflowStatus(k))}
                datasets={[{ label: 'Sites', data: Object.values(pendingWork.by_workflow_status).map((v) => toNumber(v)) }]}
                horizontal
                showLegend={false}
              />
            </div>
          )}
        </SectionCard>
      )}

      {/* Pending Work — Activity & Project level */}
      {pendingWork && (pendingActivity.length > 0 || pendingProject.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {pendingActivity.length > 0 && (
            <SectionCard title="Pending Work — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={levelColumns('Activity', PENDING_STATUSES)} data={pendingActivity} maxHeight="320px" emptyMessage="No pending sites" />
            </SectionCard>
          )}
          {pendingProject.length > 0 && (
            <SectionCard title="Pending Work — Project Level" icon={FaBuilding}>
              <DataTable columns={levelColumns('Project', PENDING_STATUSES)} data={pendingProject} maxHeight="320px" emptyMessage="No pending sites" />
            </SectionCard>
          )}
        </div>
      )}

      {/* Pending Work — User level */}
      {pendingWork?.user_level && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard title="Report Makers — In Progress" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'report_designer_username', label: 'Report Maker' },
                { key: 'count', label: 'Sites', align: 'center' },
              ]}
              data={pendingWork.user_level.report_maker_wise ?? rmWise}
              maxHeight="260px"
              emptyMessage="No active report makers"
            />
          </SectionCard>
          <SectionCard title="Validators — Validation Pending" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'validator_username', label: 'Validator' },
                { key: 'count', label: 'Sites', align: 'center' },
              ]}
              data={pendingWork.user_level.validator_wise ?? validatorWise}
              maxHeight="260px"
              emptyMessage="No active validators"
            />
          </SectionCard>
          <SectionCard title="TA — Review Completed (awaiting RM)" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'ta_username', label: 'TA' },
                { key: 'count', label: 'Sites', align: 'center' },
              ]}
              data={pendingWork.user_level.ta_wise ?? []}
              maxHeight="260px"
              emptyMessage="No TA pending hand-offs"
            />
          </SectionCard>
        </div>
      )}

      {/* ===== Today Progress ===== */}
      {todayProgress && (
        <SectionCard title="Today's Progress — Group Stage" subtitle={formatDate(todayProgress.date)} icon={FaCheckDouble}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MiniStat label="Survey Done" value={todayProgress.group_stage?.survey_done ?? 0} color="blue" />
            <MiniStat label="Field Work Approved" value={todayProgress.group_stage?.field_work_approved ?? 0} color="green" />
            <MiniStat label="Report Completed" value={todayProgress.group_stage?.report_completed ?? 0} color="yellow" />
            <MiniStat label="Report Finalized" value={todayProgress.group_stage?.report_finalized ?? 0} color="green" />
            <MiniStat label="Report Sent" value={todayProgress.group_stage?.report_sent ?? 0} color="green" />
          </div>
        </SectionCard>
      )}

      {todayProgress && ((todayProgress.activity_level?.length ?? 0) > 0 || (todayProgress.project_level?.length ?? 0) > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {(todayProgress.activity_level?.length ?? 0) > 0 && (
            <SectionCard title="Today — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={levelColumns('Activity', PROGRESS_COLUMNS)} data={todayProgress.activity_level} maxHeight="320px" emptyMessage="No activity progress today" />
            </SectionCard>
          )}
          {(todayProgress.project_level?.length ?? 0) > 0 && (
            <SectionCard title="Today — Project Level" icon={FaBuilding}>
              <DataTable columns={levelColumns('Project', PROGRESS_COLUMNS)} data={todayProgress.project_level} maxHeight="320px" emptyMessage="No project progress today" />
            </SectionCard>
          )}
        </div>
      )}

      {todayProgress?.user_level && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard title="Report Makers — Completed Today" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'name', label: 'Report Maker' },
                { key: 'count', label: 'Reports', align: 'center' },
              ]}
              data={todayProgress.user_level.report_maker_wise ?? []}
              maxHeight="260px"
              emptyMessage="No reports completed today"
            />
          </SectionCard>
          <SectionCard title="Validators — Finalized Today" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'name', label: 'Validator' },
                { key: 'count', label: 'Finalized', align: 'center' },
              ]}
              data={todayProgress.user_level.validator_wise ?? []}
              maxHeight="260px"
              emptyMessage="No reports finalized today"
            />
          </SectionCard>
          <SectionCard title="MIS — Sent to Client Today" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'name', label: 'MIS' },
                { key: 'count', label: 'Sent', align: 'center' },
              ]}
              data={todayProgress.user_level.mis_wise ?? []}
              maxHeight="260px"
              emptyMessage="Nothing dispatched today"
            />
          </SectionCard>
        </div>
      )}

      {/* ===== Rejection Pending ===== */}
      {rejectionPending && (
        <SectionCard title="Rejection Pending — Workflow Stage" icon={FaExclamationTriangle}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MiniStat label="TA Rejected" value={rejectionPending.by_workflow_status?.ta_rejected ?? 0} color="red" />
            <MiniStat label="Report Maker Rejected" value={rejectionPending.by_workflow_status?.report_maker_rejected ?? 0} color="red" />
            <MiniStat label="Validation Rejected" value={rejectionPending.by_workflow_status?.validation_rejected ?? 0} color="red" />
          </div>
        </SectionCard>
      )}

      {rejectionPending && (rejectActivity.length > 0 || rejectProject.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {rejectActivity.length > 0 && (
            <SectionCard title="Rejections — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={levelColumns('Activity', REJECT_STATUSES)} data={rejectActivity} maxHeight="280px" emptyMessage="No rejections" />
            </SectionCard>
          )}
          {rejectProject.length > 0 && (
            <SectionCard title="Rejections — Project Level" icon={FaBuilding}>
              <DataTable columns={levelColumns('Project', REJECT_STATUSES)} data={rejectProject} maxHeight="280px" emptyMessage="No rejections" />
            </SectionCard>
          )}
        </div>
      )}

      {rejectionPending?.user_level && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard title="Report Makers — Rejected" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'report_designer_username', label: 'Report Maker' },
                { key: 'count', label: 'Rejected', align: 'center' },
              ]}
              data={rejectionPending.user_level.report_maker_wise ?? []}
              maxHeight="260px"
              emptyMessage="No report-maker rejections"
            />
          </SectionCard>
          <SectionCard title="Validators — Rejected" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'validator_username', label: 'Validator' },
                { key: 'count', label: 'Rejected', align: 'center' },
              ]}
              data={rejectionPending.user_level.validator_wise ?? []}
              maxHeight="260px"
              emptyMessage="No validation rejections"
            />
          </SectionCard>
          <SectionCard title="TA — Rejected" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'ta_username', label: 'TA' },
                { key: 'count', label: 'Rejected', align: 'center' },
              ]}
              data={rejectionPending.user_level.ta_wise ?? []}
              maxHeight="260px"
              emptyMessage="No TA rejections"
            />
          </SectionCard>
        </div>
      )}

      {/* ===== Kept: unassigned action lists ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Pending RM Assignment" icon={FaHourglassHalf}>
          <DataTable
            columns={[
              { key: 'site_id', label: 'Site ID' },
              { key: 'site_name', label: 'Name' },
              { key: 'activity', label: 'Activity', render: (item) => (item as any).activity || '—' },
              { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
              { key: 'ta_work_date', label: 'TA Date', render: (item) => formatDate(item.ta_work_date) },
            ]}
            data={rmAssignPending}
            maxHeight="280px"
            emptyMessage="No pending RM assignments"
          />
        </SectionCard>

        <SectionCard title="Pending Validator Assignment" icon={FaUserTie}>
          <DataTable
            columns={[
              { key: 'site_id', label: 'Site ID' },
              { key: 'site_name', label: 'Name' },
              { key: 'report_maker', label: 'Report Maker' },
              { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
            ]}
            data={validatorAssignPending}
            maxHeight="280px"
            emptyMessage="No sites pending validator"
          />
        </SectionCard>

        <SectionCard title="Pending Send To Client" icon={FaCheckDouble}>
          <DataTable
            columns={[
              { key: 'site_id', label: 'Site ID' },
              { key: 'site_name', label: 'Name' },
              { key: 'validator', label: 'Validator' },
              { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
            ]}
            data={sentToClientPending}
            maxHeight="280px"
            emptyMessage="No finalized sites pending dispatch"
          />
        </SectionCard>
      </div>

      {/* ===== Kept: Billing ===== */}
      {billing?.by_status?.length > 0 && (
        <SectionCard title="Billing Status" icon={FaFileInvoice}>
          <DataTable
            columns={[
              { key: 'billing_status', label: 'Status', render: (item) => <StatusBadge status={item.billing_status} /> },
              { key: 'count', label: 'Count' },
            ]}
            data={billing.by_status}
            maxHeight="200px"
            emptyMessage="No billing data"
          />
        </SectionCard>
      )}
    </div>
  );
};

export default MISView;